/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import { HashLink as Link } from "react-router-hash-link";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Gütertrennung_700px.jpg";
import { gaSet } from "../../../services/cookieService";
import Typography from "@mui/material/Typography";
import { Box, Link as MuiLink, Paper, TableHead } from "@mui/material";
import MuiListNativeStyled from "../../List/MuiListNativeStyled";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

const GuidebookDivorcePropertyScheme = () => {
  const title = "Ratgeber - Güterstand | Legalbird";
  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Erfahren Sie bei uns alles, was Sie zum gesetzlichen Güterstand,
        Zugewinngemeinschaft und Gütertrennung wissen müssen."
        />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan
          slogan={"Wie wird der Besitz aufgeteilt? Alles zum Thema Gütertrennung"}
          backgroundImagePath={backgroundTeaser}
          height={300}
          headingColor={"#333333"}
        />
        <TextContainer>
          <p>
            Bei einer Scheidung ist es sehr wichtig zu wissen, wie die gemeinsamen Besitztümer und das Vermögen aufgeteilt werden. Wem das in die Ehe
            eingebrachte und während der Ehe erwirtschaftete Vermögen gehört und wie es bei einer Scheidung aufgeteilt wird, ist von der Art des Güterstands
            abhängig.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Drei Arten des Güterstands
          </Typography>
          <p>Es gibt drei Möglichkeiten, wie der Güterstand in einer Ehe und somit auch die Vermögenstrennung im Falle einer Scheidung geregelt sein kann:</p>
          <MuiListNativeStyled
            sx={{ listStyle: "decimal" }}
            listItems={[
              <>
                <MuiLink component={Link} sx={guidebookContentStyle.link} to="/ratgeber/familienrecht/gueterstand/#zugewinngemeinschaft">
                  Gesetzlicher Güterstand/Zugewinngemeinschaft
                </MuiLink>{" "}
                - nur der Zugewinn während der Ehe wird aufgeteilt
              </>,
              <>
                <MuiLink component={Link} sx={guidebookContentStyle.link} to="/ratgeber/familienrecht/gueterstand/#guetertrennung">
                  Gütertrennung
                </MuiLink>{" "}
                - jeder Ehepartner behält sein eigenes Vermögen
              </>,
              <>
                <MuiLink component={Link} sx={guidebookContentStyle.link} to="/ratgeber/familienrecht/gueterstand/#guetergemeinschaft">
                  Gütergemeinschaft
                </MuiLink>{" "}
                - das gesamte Vermögen gehört beiden Ehepartnern gemeinschaftlich
              </>,
            ]}
          />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"zugewinngemeinschaft"} />
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            1. Was ist der gesetzliche Güterstand/die Zugewinngemeinschaft?
          </Typography>
          <p>
            Wenn kein Ehevertrag mit anderen Regelungen bezüglich des Güterstands vorliegt, gilt in Deutschland der gesetzliche Güterstand und somit die
            Zugewinngemeinschaft. Bei der Zugewinngemeinschaft gehen die vorehelichen Besitztümer der Ehepartner bei einer Heirat nicht mit in das Eigentum des
            Partners über.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Was muss bei einer Scheidung beachtet werden?
          </Typography>
          <p>
            In einer Zugewinngemeinschaft bleibt jeder Ehegatte Eigentümer des Vermögens, das er in die Ehe eingebracht hat und das er während der Ehe
            hinzugewinnt. Jeder Ehepartner haftet daher auch selber für seine vor und während der Ehe entstandenen Schulden.
          </p>
          <p>
            Im Fall einer Scheidung kommt es zu einem Ausgleich des hinzugewonnenen Vermögens (Zugewinn). Das von den Ehepartnern mit in die Ehe eingebrachte
            Vermögen wird nicht ausgeglichen. Das Begleichen von Schulden während der Ehe stellt einen Zugewinn von Vermögen dar und muss somit mit in den
            Zugewinn einberechnet werden.
          </p>
          <p>
            Wenn sich das Vermögen eines Ehepartners während der Ehe stärker erhöht hat als das des anderen Ehepartners, muss bei einer Scheidung auf Verlangen
            ein Vermögensausgleich gezahlt werden. Die Höhe des Vermögensausgleichs beträgt die Hälfte des Zugewinns. Der Anspruch auf Zugewinnausgleich muss
            aktiv geltend gemacht werden. Sie sollten sich hierüber am Besten mit Ihrem Ehepartner einigen. Im Scheidungsverfahren wird grundsätzlich nur über
            die Scheidung und den Versorgungsausgleich entschieden. Das Gericht entscheidet also nicht automatisch über den Zugewinn. Sollten Sie zu keiner
            Einigung mit Ihrem Ehepartner kommen, können Sie einen Anspruch natürlich gerichtlich geltend machen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Wie berechnet sich der Zugewinnausgleich?
          </Typography>
          <p>
            Für den Zugwinnausgleich werden die Gesamtvermögen der Ehepartner miteinander verglichen. Hierzu muss der Vermögenszugewinn während der Ehe für
            beide Ehepartner bestimmt werden. Pro Ehepartner wird das Anfangsvermögen im Zeitpunkt der Eheschließung vom Endvermögen nach der Ehe abgezogen.
            Laut Güterrecht wird als Stichtag für die Ermittlung des Endvermögens der Tag verwendet, an dem der Scheidungsantrag zugestellt wird.
          </p>
          <p>
            Wichtig: Schenkungen und Erbschaften während der Ehe werden nicht in das Endvermögen einberechnet. Wurde der Vermögenszugewinn für beide Parteien
            berechnet, muss der Ehepartner mit dem höheren Zugewinn die Hälfte des Überschusses an den anderen bezahlen.
          </p>
          <HighlightBox headline={"Rechenbeispiel für den Zugewinnausgleich"}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues} scope={"col"}>
                    Beschreibung
                  </TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues} scope={"col"}>
                    Betrag
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Vermögen der Ehefrau vor der Ehe</TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>10.000 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Vermögen der Ehefrau nach der Ehe</TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>25.000 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Vermögenszugewinn der Ehefrau</TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>15.000 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>
                    <br />
                  </TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>
                    <br />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Vermögen des Ehemanns vor der Ehe</TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>30.000 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Vermögen des Ehemanns nach der Ehe</TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>35.000 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Vermögenszugewinn des Ehemanns</TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>5.000 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>
                    <br />
                  </TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>
                    <br />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Mehrvermögen der Ehefrau nach der Ehe (15.000 € - 5.000 €)</TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>10.000 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Zugewinnausgleich (Mehrvermögen geteilt durch 2)</TableCell>
                  <TableCell sx={guidebookContentStyle.tableValues}>5.000 €</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <p>
              Da der Zugewinn zwischen den Ehepartnern bei einer Scheidung ausgeglichen werden muss, kann der Ehemann von der Ehefrau bei deren gegebenem
              Mehrvermögen von 10.000 € einen Zugewinnausgleich von 5.000 € verlangen.
            </p>
          </HighlightBox>
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"guetertrennung"} />
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            2. Was ist Gütertrennung?
          </Typography>
          <p>
            Durch einen notariellen Ehevertrag können Ehepartner in den Güterstand der Gütertrennung eintreten. Somit bleibt der gesamte Besitz im Eigentum des
            jeweiligen Partners. Das vor und während der Ehe erworbene Vermögen und auch die Schulden der Ehepartner bleiben trotz Heirat vollständig getrennt.
            Im Falle einer Scheidung kann daher kein Anspruch auf einen Ausgleich gestellt werden - ein Zugewinnausgleich entfällt.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Was muss bei einer Scheidung beachtet werden?
          </Typography>
          <p>Jeglicher Besitz, der als alleiniges Eigentum einem der Ehepartner zugewiesen werden kann, bleibt bei der Aufteilung der Güter unbeachtet.</p>
          <p>
            Durch eine Gütertrennung ist jedoch nicht generell jeder vermögensrechtliche Ausgleich ausgeschlossen. Gemeinsam erworbenes Vermögen, Hausrat und
            gemeinschaftliche Schulden müssen ausgeglichen werden.
          </p>
          <p>
            Gemeinsamer Besitz wird in der Regel im Zuge des vermögensrechtlichen Ausgleichs zu gleichen Teilen auf die Ehepartner aufgeteilt - gleiches gilt
            für den gemeinsamen Hausrat. Zum gemeinsamen Hausrat gehören (bei gemeinschaftlicher Nutzung) beispielsweise das Auto oder das Mobiliar.
          </p>
          <p>
            Bei der Gütertrennung haftet der Ehepartner allgemein nicht für die Schulden seines Ehegatten. Wenn die Ehepartner jedoch während der Ehe gemeinsam
            Schulden aufgenommen haben, müssen sie diese auch nach der Scheidung zu gleichen Teilen begleichen und sind für diese haftbar.
          </p>
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"guetergemeinschaft"} />
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            3. Was ist eine Gütergemeinschaft?
          </Typography>
          <p>
            Ehepartner können per Ehevertrag den Güterstand der Gütergemeinschaft vereinbaren. In einer Gütergemeinschaft wird jeglicher Besitz der Ehepartner
            bei einer Heirat zum gemeinschaftlichen Besitz (Gesamtgut). Über das gemeinsame Vermögen dürfen die Ehepartner, wenn nicht anders vertraglich
            definiert, nur gemeinsam verfügen. Auch für Schulden haften beide Ehepartner gemeinsam.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Was muss bei einer Scheidung beachtet werden?
          </Typography>
          <p>
            Im Scheidungsfall muss jeglicher Besitz zwischen den Ehepartnern gleich aufgeteilt werden. Kein Ehepartner darf alleine über die Gegenstände und das
            Vermögen verfügen (z.B. etwas verkaufen), bis sich beide einvernehmlich über die Teilung des Güterstandes geeinigt haben.
          </p>
          <p>
            Auch persönliche Gegenstände, die eventuell klar einem Ehepartner zugewiesen werden können (wie zum Beispiel Schmuck), müssen gleichmäßig aufgeteilt
            werden.
          </p>
          <p>
            Bei einer Gütergemeinschaft haftet der Ehepartner zusätzlich zu gemeinsamen Schulden auch für die Schulden seines Ehegatten. Nach einer Scheidung
            sind demnach auch beide Ehepartner für die Schulden haftbar und müssen diese zu gleichen Teilen begleichen.
          </p>
          <DoubleButton buttonText={"zum Scheidungsantrag"} buttonLink={"/servicewelt"} backText={"Zurück zum Ratgeber"} backLink={"/ratgeber/familienrecht"} />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookDivorcePropertyScheme;
